import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, BatteryCharging, Gauge, Users, Fuel, Check, ExternalLink, ArrowLeft, ArrowRight } from "lucide-react";
import { useLead } from "../../context/LeadContext";
import { formatCHF } from "../../lib/format";

export default function ModelDetail({ model, onClose }) {
  const { requestOfferte } = useLead();

  useEffect(() => {
    document.body.style.overflow = model ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [model]);

  const specs = model
    ? [
        { icon: Fuel, label: "Antrieb", value: model.drivetrains.join(", ") },
        model.power ? { icon: Gauge, label: "Leistung", value: model.power } : null,
        model.range_km ? { icon: BatteryCharging, label: "Reichweite (WLTP)", value: `bis ${model.range_km} km` } : null,
        model.consumption ? { icon: Fuel, label: "Verbrauch", value: model.consumption } : null,
        model.seats ? { icon: Users, label: "Sitzplätze", value: `${model.seats}` } : null,
      ].filter(Boolean)
    : [];

  return (
    <AnimatePresence>
      {model && (
        <motion.div
          data-testid="model-detail-overlay"
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-white overflow-y-auto"
        >
          {/* Sticky Modell-Header */}
          <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-neutral-200">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
              <button
                data-testid="model-detail-close"
                onClick={onClose}
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-950 transition-colors"
              >
                <ArrowLeft size={16} /> Zur Übersicht
              </button>
              <div className="hidden sm:block font-display font-semibold text-neutral-950 tracking-tight">{model.name}</div>
              <button
                data-testid="model-detail-offerte-top"
                onClick={() => { onClose(); requestOfferte(model.name); }}
                className="bg-neutral-950 text-white text-xs font-semibold px-5 py-2.5 transition-colors hover:bg-neutral-800"
              >
                Firmenofferte
              </button>
            </div>
          </div>

          {/* Hero */}
          <div className="relative h-[60vh] sm:h-[72vh] bg-neutral-100 overflow-hidden">
            <motion.img
              initial={{ scale: 1.06 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              src={model.image_url}
              alt={model.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-5 sm:px-8 pb-10 sm:pb-14">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-white/70">
                {model.category}{model.variant ? ` · ${model.variant}` : ""}
              </div>
              <h2 className="mt-2 text-4xl sm:text-6xl lg:text-7xl font-medium tracking-tighter text-white">{model.name}</h2>
              <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3">
                <div className="text-white" data-testid="model-detail-price">
                  <span className="text-sm text-white/70">ab </span>
                  <span className="text-2xl font-semibold tracking-tight">
                    {model.price_from ? formatCHF(model.price_from) : "Preis auf Anfrage"}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    data-testid="model-detail-offerte"
                    onClick={() => { onClose(); requestOfferte(model.name); }}
                    className="group inline-flex items-center gap-2 bg-white text-neutral-950 text-sm font-semibold px-6 py-3 transition-colors hover:bg-neutral-200"
                  >
                    Firmenofferte anfordern
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  <a
                    data-testid="model-detail-kia-link"
                    href={model.kia_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-white/50 text-white text-sm font-semibold px-6 py-3 transition-colors hover:bg-white/10"
                  >
                    Auf KIA.ch ansehen <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Facts-Leiste */}
          <div className="border-b border-neutral-200">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
              {specs.map((s) => (
                <div key={s.label} className="border-l border-neutral-200 last:border-r p-5 sm:p-6">
                  <s.icon size={16} className="text-neutral-400" />
                  <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">{s.label}</div>
                  <div className="mt-1 text-sm font-semibold text-neutral-950">{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Highlights (KIA-Stil: wechselnde Bild/Text-Anordnung) */}
          {model.highlights?.length > 0 && (
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-neutral-500 mb-12">Highlights</div>
              <div className="space-y-16 sm:space-y-24">
                {model.highlights.map((h, i) => (
                  <div key={i} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center`}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className={`overflow-hidden bg-neutral-100 ${i % 2 === 1 ? "lg:order-2" : ""}`}
                    >
                      <img src={h.image} alt={`${model.name} – ${h.title}`} loading="lazy" className="w-full aspect-[16/10] object-cover" />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                      className={i % 2 === 1 ? "lg:order-1" : ""}
                    >
                      <div className="text-xs font-semibold tracking-[0.25em] text-neutral-400">{String(i + 1).padStart(2, "0")}</div>
                      <h3 className="mt-3 text-2xl sm:text-3xl font-medium tracking-tight text-neutral-950">{h.title}</h3>
                      <p className="mt-4 text-base leading-relaxed text-neutral-600 max-w-md">{h.text}</p>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Technische Daten */}
          <div className="bg-neutral-950 text-white py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-neutral-500 mb-10">Technische Daten</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-800">
                {specs.map((s) => (
                  <div key={s.label} className="border-b border-r border-neutral-800 p-7">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">{s.label}</div>
                    <div className="mt-2 text-xl font-semibold tracking-tight">{s.value}</div>
                  </div>
                ))}
                <div className="border-b border-r border-neutral-800 p-7">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">Garantie</div>
                  <div className="mt-2 text-xl font-semibold tracking-tight">7 Jahre KIA Herstellergarantie*</div>
                </div>
              </div>
              <p className="mt-6 text-[11px] leading-relaxed text-neutral-500 max-w-2xl">
                *Gemäss den aktuellen KIA Garantiebedingungen für die Schweiz. Alle Angaben ohne Gewähr – technische Daten gemäss KIA Schweiz, Änderungen vorbehalten.
              </p>
            </div>
          </div>

          {/* Preis & Vorteile für Geschäftskunden */}
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-neutral-500 mb-6">Vorteile für Geschäftskunden</div>
              <ul className="space-y-4">
                {(model.fleet_benefits || []).map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base text-neutral-700">
                    <Check size={18} className="mt-0.5 shrink-0 text-neutral-950" /> {b}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm leading-relaxed text-neutral-600 max-w-lg">{model.description}</p>
              <a
                href={model.kia_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-neutral-950 border-b border-neutral-950 pb-1 hover:opacity-60 transition-opacity"
              >
                Mehr Informationen auf KIA.ch <ExternalLink size={14} />
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-24 bg-neutral-50 border border-neutral-200 p-8">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-neutral-500">Preis</div>
                <div className="mt-2 text-3xl font-semibold tracking-tight text-neutral-950">
                  {model.price_from ? `ab ${formatCHF(model.price_from)}` : "auf Anfrage"}
                </div>
                <p className="mt-2 text-[10px] leading-relaxed text-neutral-400">
                  Unverbindlicher Listenpreis gemäss KIA Schweiz. Preisänderungen vorbehalten.
                </p>
                <button
                  data-testid="model-detail-offerte-bottom"
                  onClick={() => { onClose(); requestOfferte(model.name); }}
                  className="mt-6 w-full bg-neutral-950 text-white text-sm font-semibold px-6 py-4 transition-colors hover:bg-neutral-800"
                >
                  Firmenofferte anfordern
                </button>
                <p className="mt-4 text-xs text-neutral-500 leading-relaxed">
                  Wir prüfen individuell, welche Firmenkonditionen für Ihr Unternehmen möglich sind – kostenlos und unverbindlich.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
